import * as client from "../../client";
import { AttachmentMapping, effectiveMapping } from "./attachments/mapping";

// Return a stable, comparable key for sorting ReportContact,
// with support for a company_info fallback.
export const brokerSortValue = (
  contact: client.ReportContact | null | undefined,
  company_info: client.RawCompanyInfo
): string => {
  if (!contact) {
    return (company_info.broker_name ?? "").toLowerCase();
  }
  return (
    contact.full_name ||
    contact.organization_name ||
    contact.email ||
    company_info.broker_name ||
    ""
  ).toLowerCase();
};

/** Append "Z" if the timestamp lacks a timezone so the browser treats it as UTC. */
export const ensureUtc = (ts: string): string =>
  ts.endsWith("Z") ? ts : ts + "Z";

export const formatTimestamp = (ts: string): string => {
  const d = new Date(ensureUtc(ts));
  return d.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

/**
 * Format to two decimals, but show "<0.01" for tiny positive values that
 * would otherwise round to "0.00" and read as zero.
 */
export const formatTwoDecimals = (n: number): string =>
  n > 0 && n < 0.005 ? "<0.01" : n.toFixed(2);

/** Every source that produced extracted policy terms, whose limits, deductibles
 *  and sublimits can therefore be viewed and edited. */
export const getPolicySourceNames = (
  sublimitInfo: client.SublimitInfo | null | undefined,
  deductibleInfo: client.AdvancedDeductibleInfo | null | undefined,
  limitInfo: client.AdvancedLimitInfo | null | undefined
): string[] => [
  ...new Set([
    ...Object.keys(sublimitInfo?.extracted_sublimits ?? {}),
    ...Object.keys(deductibleInfo?.extracted_deductibles ?? {}),
    ...Object.keys(limitInfo?.extracted_limits ?? {}),
  ]),
];

// Mirrors EMAIL_BODY_SOURCE_NAME in the backend's types.py.
export const EMAIL_BODY_SOURCE_NAME = "Email Body";

// Classifications that can be chosen as the policy form. Mirrors
// Attachment.is_selectable_policy_document() in the backend's types.py
// (POLICY_FORM_ATTACHMENT_TYPES plus specs_sheet); keep the two in sync.
const SELECTABLE_POLICY_FORM_MAPPINGS: ReadonlySet<AttachmentMapping> =
  new Set<AttachmentMapping>([
    "policy_document",
    "manuscript_form",
    "final_manuscript_form",
    "specs_sheet",
  ]);

/** The forms choosable as *the* policy form, in the order the backend derives
 *  its default from. Mirrors derive_selected_policy_form_name. */
export const getSelectablePolicyFormNames = (
  attachmentInfo: client.AttachmentInfo | null | undefined,
  deductibleInfo: client.AdvancedDeductibleInfo | null | undefined,
  limitInfo: client.AdvancedLimitInfo | null | undefined
): string[] => {
  // Keyed off classification, not which sources produced terms, because that is
  // how the backend validates the selection. Keying off extracted terms would
  // offer forms it rejects (a document reclassified away whose terms linger
  // until a rerun) and hide ones it accepts (a policy document extraction found
  // nothing in), blanking the dropdown on a form that really is selected.
  const names = (attachmentInfo?.attachments ?? [])
    .filter((attachment) =>
      SELECTABLE_POLICY_FORM_MAPPINGS.has(effectiveMapping(attachment))
    )
    .map((attachment) => attachment.name);
  // The email body is only a policy source when it yielded limits or
  // deductibles. Sublimits alone don't count, matching the backend.
  const emailBodyHasTerms =
    Object.keys(
      deductibleInfo?.extracted_deductibles?.[EMAIL_BODY_SOURCE_NAME] ?? {}
    ).length > 0 ||
    Object.keys(limitInfo?.extracted_limits?.[EMAIL_BODY_SOURCE_NAME] ?? {})
      .length > 0;
  return emailBodyHasTerms ? [...names, EMAIL_BODY_SOURCE_NAME] : names;
};
